/**
 * 品項模組 - 輔助函式
 * Equipment Item Module - Helper Functions
 */

import {
  EquipmentItem,
  EquipmentItemWithRelations,
  EquipmentItemStatus,
  EquipmentItemStats,
  EquipmentItemFilter,
  EquipmentItemHistoryAction,
  EquipmentItemStatusConfig,
  EquipmentItemHistoryActionConfig,
  EQUIPMENT_ITEM_STATUS,
  EQUIPMENT_ITEM_STATUS_CONFIG,
  EQUIPMENT_ITEM_HISTORY_ACTIONS,
} from './types';

/**
 * 取得狀態配置
 * @param status 狀態 ID
 * @returns 狀態配置
 */
export function getStatusConfig(status: EquipmentItemStatus): EquipmentItemStatusConfig {
  return EQUIPMENT_ITEM_STATUS_CONFIG.find(s => s.id === status) || EQUIPMENT_ITEM_STATUS_CONFIG[0];
}

/**
 * 取得狀態名稱
 * @param status 狀態 ID
 * @param lang 語言（'zh' | 'en'）
 * @returns 狀態名稱
 */
export function getStatusName(status: EquipmentItemStatus, lang: 'zh' | 'en' = 'zh'): string {
  const config = getStatusConfig(status);
  return lang === 'en' ? config.nameEn : config.name;
}

/**
 * 取得狀態顏色類別
 * @param status 狀態 ID
 * @returns 顏色類別字串
 */
export function getStatusColorClass(status: EquipmentItemStatus): string {
  const config = getStatusConfig(status);
  return `${config.bgLight} ${config.textColor}`;
}

/**
 * 取得狀態圖示
 * @param status 狀態 ID
 * @returns 圖示類別
 */
export function getStatusIcon(status: EquipmentItemStatus): string {
  const config = getStatusConfig(status);
  return config.icon;
}

/**
 * 取得歷史記錄動作配置
 * @param action 動作類型
 * @returns 動作配置
 */
export function getHistoryActionConfig(action: EquipmentItemHistoryAction): EquipmentItemHistoryActionConfig {
  return EQUIPMENT_ITEM_HISTORY_ACTIONS.find(a => a.action === action) || {
    action: action,
    label: action,
    icon: 'ri-information-line',
    color: 'text-gray-600 bg-gray-100'
  };
}

/**
 * 取得歷史記錄動作標籤
 * @param action 動作類型
 * @returns 動作標籤
 */
export function getHistoryActionLabel(action: EquipmentItemHistoryAction): string {
  return getHistoryActionConfig(action).label;
}

/**
 * 根據狀態變更判斷歷史記錄動作類型
 * @param oldStatus 舊狀態
 * @param newStatus 新狀態
 * @returns 動作類型
 */
export function determineStatusChangeAction(
  oldStatus: EquipmentItemStatus,
  newStatus: EquipmentItemStatus
): EquipmentItemHistoryAction {
  if (newStatus === EQUIPMENT_ITEM_STATUS.MAINTENANCE) {
    return 'maintenance_start';
  }
  if (oldStatus === EQUIPMENT_ITEM_STATUS.MAINTENANCE && newStatus === EQUIPMENT_ITEM_STATUS.AVAILABLE) {
    return 'maintenance_end';
  }
  if (newStatus === EQUIPMENT_ITEM_STATUS.RETIRED) {
    return 'retired';
  }
  if (newStatus === EQUIPMENT_ITEM_STATUS.RENTED) {
    return 'order_rented';
  }
  if (oldStatus === EQUIPMENT_ITEM_STATUS.RENTED && newStatus === EQUIPMENT_ITEM_STATUS.AVAILABLE) {
    return 'order_returned';
  }
  return 'status_change';
}

/**
 * 計算品項統計
 * @param items 品項列表
 * @returns 統計資料
 */
export function calculateItemStats(items: EquipmentItem[]): EquipmentItemStats {
  return {
    total: items.length,
    available: items.filter(i => i.status === EQUIPMENT_ITEM_STATUS.AVAILABLE).length,
    rented: items.filter(i => i.status === EQUIPMENT_ITEM_STATUS.RENTED).length,
    maintenance: items.filter(i => i.status === EQUIPMENT_ITEM_STATUS.MAINTENANCE).length,
    retired: items.filter(i => i.status === EQUIPMENT_ITEM_STATUS.RETIRED).length,
  };
}

/**
 * 計算特定產品的品項統計
 * @param items 品項列表
 * @param productId 產品 ID
 * @returns 統計資料
 */
export function calculateProductItemStats(items: EquipmentItem[], productId: string): EquipmentItemStats {
  const productItems = items.filter(i => i.product_id === productId);
  return calculateItemStats(productItems);
}

/**
 * 篩選品項
 * @param items 品項列表
 * @param filter 篩選條件
 * @returns 篩選後的品項列表
 */
export function filterItems(
  items: EquipmentItemWithRelations[],
  filter: EquipmentItemFilter
): EquipmentItemWithRelations[] {
  return items.filter(item => {
    // 產品篩選
    if (filter.productId && item.product_id !== filter.productId) {
      return false;
    }
    
    // 門市篩選
    if (filter.locationId && item.location_id !== filter.locationId) {
      return false;
    }
    
    // 狀態篩選
    if (filter.status && item.status !== filter.status) {
      return false;
    }
    
    // 搜尋篩選
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      const matchSerial = item.serial_number.toLowerCase().includes(searchLower);
      const matchProductName = item.product?.name_zh?.toLowerCase().includes(searchLower) || false;
      const matchBrand = item.product?.brand?.toLowerCase().includes(searchLower) || false;
      
      if (!matchSerial && !matchProductName && !matchBrand) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * 產生自動序號
 * @param prefix 前綴
 * @param startNumber 起始編號
 * @param quantity 數量
 * @param padLength 編號位數（預設 3）
 * @returns 序號列表
 */
export function generateSerialNumbers(
  prefix: string,
  startNumber: number,
  quantity: number,
  padLength: number = 3
): string[] {
  const serials: string[] = [];
  for (let i = 0; i < quantity; i++) {
    const number = startNumber + i;
    const paddedNumber = String(number).padStart(padLength, '0');
    serials.push(`${prefix}-${paddedNumber}`);
  }
  return serials;
}

/**
 * 從品牌名稱產生序號前綴
 * @param brandName 品牌名稱
 * @param maxLength 最大長度（預設 3）
 * @returns 前綴
 */
export function generatePrefixFromBrand(brandName: string, maxLength: number = 3): string {
  return brandName.substring(0, maxLength).toUpperCase();
}

/**
 * 驗證序號格式
 * @param serialNumber 序號
 * @returns 是否有效
 */
export function isValidSerialNumber(serialNumber: string): boolean {
  // 序號不能為空，且長度至少 3 個字元
  if (!serialNumber || serialNumber.trim().length < 3) {
    return false;
  }
  // 序號只能包含字母、數字、連字號和底線
  const pattern = /^[A-Za-z0-9\-_]+$/;
  return pattern.test(serialNumber.trim());
}

/**
 * 檢查序號是否重複
 * @param serialNumber 要檢查的序號
 * @param existingItems 現有品項列表
 * @param excludeId 排除的品項 ID（編輯時使用）
 * @returns 是否重複
 */
export function isSerialNumberDuplicate(
  serialNumber: string,
  existingItems: EquipmentItem[],
  excludeId?: string
): boolean {
  const normalizedSerial = serialNumber.trim().toLowerCase();
  return existingItems.some(item => 
    item.serial_number.toLowerCase() === normalizedSerial &&
    item.id !== excludeId
  );
}

/**
 * 格式化購入價格
 * @param price 價格
 * @returns 格式化字串
 */
export function formatPurchasePrice(price: number | null): string {
  if (price === null || price === undefined) {
    return '-';
  }
  return `NT$ ${price.toLocaleString()}`;
}

/**
 * 格式化購入日期
 * @param date 日期字串
 * @returns 格式化字串
 */
export function formatPurchaseDate(date: string | null): string {
  if (!date) {
    return '-';
  }
  return date;
}

/**
 * 格式化日期時間
 * @param dateString ISO 日期字串
 * @returns 格式化字串
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * 取得門市名稱
 * @param locationId 門市 ID
 * @param locations 門市列表
 * @returns 門市名稱
 */
export function getLocationName(
  locationId: string | null,
  locations: Array<{ id: string; name_zh: string }>
): string {
  if (!locationId) {
    return '未指定';
  }
  const location = locations.find(l => l.id === locationId);
  return location?.name_zh || '-';
}

/**
 * 檢查品項是否可租借
 * @param item 品項
 * @returns 是否可租借
 */
export function isItemAvailable(item: EquipmentItem): boolean {
  return item.status === EQUIPMENT_ITEM_STATUS.AVAILABLE;
}

/**
 * 檢查品項是否已租出
 * @param item 品項
 * @returns 是否已租出
 */
export function isItemRented(item: EquipmentItem): boolean {
  return item.status === EQUIPMENT_ITEM_STATUS.RENTED;
}

/**
 * 檢查品項是否維修中
 * @param item 品項
 * @returns 是否維修中
 */
export function isItemInMaintenance(item: EquipmentItem): boolean {
  return item.status === EQUIPMENT_ITEM_STATUS.MAINTENANCE;
}

/**
 * 檢查品項是否已報廢
 * @param item 品項
 * @returns 是否已報廢
 */
export function isItemRetired(item: EquipmentItem): boolean {
  return item.status === EQUIPMENT_ITEM_STATUS.RETIRED;
}

/**
 * 取得可用品項數量
 * @param items 品項列表
 * @param productId 產品 ID（可選）
 * @returns 可用數量
 */
export function getAvailableCount(items: EquipmentItem[], productId?: string): number {
  let filtered = items;
  if (productId) {
    filtered = items.filter(i => i.product_id === productId);
  }
  return filtered.filter(i => i.status === EQUIPMENT_ITEM_STATUS.AVAILABLE).length;
}

/**
 * 依狀態分組品項
 * @param items 品項列表
 * @returns 分組後的品項
 */
export function groupItemsByStatus(
  items: EquipmentItem[]
): Record<EquipmentItemStatus, EquipmentItem[]> {
  return {
    available: items.filter(i => i.status === EQUIPMENT_ITEM_STATUS.AVAILABLE),
    rented: items.filter(i => i.status === EQUIPMENT_ITEM_STATUS.RENTED),
    maintenance: items.filter(i => i.status === EQUIPMENT_ITEM_STATUS.MAINTENANCE),
    retired: items.filter(i => i.status === EQUIPMENT_ITEM_STATUS.RETIRED),
  };
}

/**
 * 依產品分組品項
 * @param items 品項列表
 * @returns 分組後的品項（key 為 product_id）
 */
export function groupItemsByProduct(
  items: EquipmentItem[]
): Record<string, EquipmentItem[]> {
  return items.reduce((acc, item) => {
    const productId = item.product_id;
    if (!acc[productId]) {
      acc[productId] = [];
    }
    acc[productId].push(item);
    return acc;
  }, {} as Record<string, EquipmentItem[]>);
}

/**
 * 依門市分組品項
 * @param items 品項列表
 * @returns 分組後的品項（key 為 location_id，null 為 'unassigned'）
 */
export function groupItemsByLocation(
  items: EquipmentItem[]
): Record<string, EquipmentItem[]> {
  return items.reduce((acc, item) => {
    const locationId = item.location_id || 'unassigned';
    if (!acc[locationId]) {
      acc[locationId] = [];
    }
    acc[locationId].push(item);
    return acc;
  }, {} as Record<string, EquipmentItem[]>);
}

/**
 * 排序品項
 * @param items 品項列表
 * @param sortBy 排序欄位
 * @param order 排序方向
 * @returns 排序後的品項列表
 */
export function sortItems(
  items: EquipmentItemWithRelations[],
  sortBy: 'serial_number' | 'created_at' | 'purchase_date' | 'purchase_price' | 'status',
  order: 'asc' | 'desc' = 'asc'
): EquipmentItemWithRelations[] {
  const sorted = [...items].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'serial_number':
        comparison = a.serial_number.localeCompare(b.serial_number);
        break;
      case 'created_at':
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
      case 'purchase_date':
        const dateA = a.purchase_date ? new Date(a.purchase_date).getTime() : 0;
        const dateB = b.purchase_date ? new Date(b.purchase_date).getTime() : 0;
        comparison = dateA - dateB;
        break;
      case 'purchase_price':
        comparison = (a.purchase_price || 0) - (b.purchase_price || 0);
        break;
      case 'status':
        const statusOrder = ['available', 'rented', 'maintenance', 'retired'];
        comparison = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
        break;
    }
    
    return order === 'desc' ? -comparison : comparison;
  });
  
  return sorted;
}

/**
 * 取得品項狀態資訊
 * @param status 狀態 ID
 * @returns 狀態資訊物件
 */
export function getItemStatusInfo(status: EquipmentItemStatus): {
  name: string;
  icon: string;
  bgLight: string;
  text: string;
} {
  const config = getStatusConfig(status);
  return {
    name: config.name,
    icon: config.icon,
    bgLight: config.bgLight,
    text: config.textColor,
  };
}

/**
 * 匯出品項資料格式化
 * @param item 品項（含關聯資料）
 * @param locations 門市列表
 * @returns 匯出格式的物件
 */
export function formatItemForExport(
  item: EquipmentItemWithRelations,
  locations: Array<{ id: string; name_zh: string }>
): Record<string, string | number> {
  const statusConfig = getStatusConfig(item.status);
  return {
    '產品名稱': item.product?.name_zh || '',
    '品牌': item.product?.brand || '',
    '分類': item.product?.category || '',
    '出廠序號': item.serial_number,
    '所在門市': getLocationName(item.location_id, locations),
    '狀態': statusConfig.name,
    '購入日期': item.purchase_date || '',
    '購入價格': item.purchase_price || '',
    '備註': item.notes || '',
    '建立時間': item.created_at ? new Date(item.created_at).toLocaleDateString('zh-TW') : ''
  };
}
